import {
  appsBomApiListAssemblies,
  appsBomApiListParts,
  appsCatalogApiListMaterials,
  getAppsBomApiListAssembliesQueryKey,
  getAppsBomApiListPartsQueryKey,
  getAppsCatalogApiListMaterialsQueryKey,
} from '@/lib/api/generated/endpoints'
import type { AssemblySchema } from '@/lib/api/generated/types.ts/assemblySchema'
import type { MaterialOut } from '@/lib/api/generated/types.ts/materialOut'
import type { PartSchema } from '@/lib/api/generated/types.ts/partSchema'
import { queryOptions } from '@tanstack/react-query'

/**
 * BOM read-only queries — flat parts / assemblies / materials catalogs.
 *
 * Why hand-rolled (not orval `*QueryOptions` factories):
 *  - orval models the response as `{ data: T, status, headers }` (its standard
 *    envelope), but our `customFetch` (lib/api/fetcher.ts) returns the parsed
 *    JSON body directly — runtime shape diverges from the OpenAPI-derived
 *    types. We cast at queryFn level.
 *  - orval factories type `queryFn` as `QueryFunction | typeof skipToken`,
 *    incompatible with `useSuspenseQuery`.
 *
 * Backend contract (apps/bom/api.py):
 *  - `GET /api/projects/{project_id}/parts` → `PartSchema[]`
 *  - `GET /api/projects/{project_id}/assemblies` → `AssemblySchema[]`
 *  - `GET /api/catalog/materials` → `MaterialOut[]` (global catalog)
 *
 * The hierarchical structure tree is fetched separately via
 * `structureTreeQuery` (see `nomenclature/queries/structure-tree-query.ts`);
 * these flat catalogs are used by Deficits, the material picker, and
 * work-order task line tables.
 */

export const projectPartsQuery = (projectId: number) =>
  queryOptions({
    queryKey: getAppsBomApiListPartsQueryKey(projectId),
    queryFn: async ({ signal }) =>
      (await appsBomApiListParts(projectId, { signal })) as unknown as readonly PartSchema[],
    staleTime: 60_000,
    enabled: Number.isFinite(projectId) && projectId > 0,
  })

/**
 * Global material catalog. Used by BOM tree + Deficits to render material
 * labels for parts. Project-agnostic post global-catalog refactor — the
 * `_projectId` parameter is preserved for prefetch-site signature
 * compatibility but unused; the catalog is fetched without scoping.
 */
export const projectMaterialsQuery = (_projectId: number) =>
  queryOptions({
    queryKey: getAppsCatalogApiListMaterialsQueryKey(),
    queryFn: async ({ signal }) =>
      (await appsCatalogApiListMaterials(undefined, {
        signal,
      })) as unknown as readonly MaterialOut[],
    staleTime: 60_000,
  })

export const projectAssembliesQuery = (projectId: number) =>
  queryOptions({
    queryKey: getAppsBomApiListAssembliesQueryKey(projectId),
    queryFn: async ({ signal }) =>
      (await appsBomApiListAssemblies(projectId, {
        signal,
      })) as unknown as readonly AssemblySchema[],
    staleTime: 60_000,
    enabled: Number.isFinite(projectId) && projectId > 0,
  })
